import { useAuthenticatedFetch } from "@stardeck-customer-apps/project-auth";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { Download, Plus, Search, Trash2, Upload } from "lucide-react";
import { useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import { AdminLayout } from "@/components/admin/admin-layout";
import { Button } from "@/components/button";
import { usePageTitle } from "@/hooks/use-page-title";

type PostType = "blog" | "case-study";

interface Post {
  id: string;
  type: string;
  title: string;
  slug: string;
  excerpt: string | null;
  status: string;
  author_name: string | null;
  created_at: string;
  published_at: string | null;
  categories: { id: string; name: string; singular_name: string | null }[];
}

interface PostCounts {
  blog: number;
  caseStudy: number;
}

export function PostsList() {
  const { type } = useParams<{ type: PostType }>();
  const postType = type === "case-study" ? "case_study" : "blog";
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const authenticatedFetch = useAuthenticatedFetch();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");

  // Page title
  const pageTitle = type === "case-study" ? "Case Studies" : "Blog Posts";
  usePageTitle(pageTitle);

  // Fetch posts
  const { data: postsData, isLoading } = useQuery({
    queryKey: ["admin-posts", postType],
    queryFn: async () => {
      const res = await authenticatedFetch(`/api/admin/posts?type=${postType}`);
      if (!res.ok) throw new Error("Failed to fetch posts");
      return res.json() as Promise<{ data: Post[] }>;
    },
  });

  // Fetch counts
  const { data: countsData } = useQuery({
    queryKey: ["admin-post-counts"],
    queryFn: async () => {
      const res = await authenticatedFetch("/api/admin/posts/counts");
      if (!res.ok) throw new Error("Failed to fetch counts");
      return res.json() as Promise<{ data: PostCounts }>;
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (postId: string) => {
      const res = await authenticatedFetch(`/api/admin/posts/${postId}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error("Failed to delete post");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-posts"] });
      queryClient.invalidateQueries({ queryKey: ["admin-post-counts"] });
      toast.success("Post deleted successfully");
    },
    onError: () => {
      toast.error("Failed to delete post");
    },
  });

  // Import mutation
  const importMutation = useMutation({
    mutationFn: async (file: File): Promise<{ data: { id: string } }> => {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("type", postType);

      const res = await authenticatedFetch("/api/admin/posts/import", {
        method: "POST",
        body: formData,
      });
      if (!res.ok) {
        const error = (await res.json()) as { message?: string };
        throw new Error(error.message || "Failed to import post");
      }
      return res.json() as Promise<{ data: { id: string } }>;
    },
    onSuccess: (data: { data: { id: string } }) => {
      queryClient.invalidateQueries({ queryKey: ["admin-posts"] });
      queryClient.invalidateQueries({ queryKey: ["admin-post-counts"] });
      toast.success("Post imported successfully");
      navigate(`/posts/${type}/${data.data.id}`);
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : "Failed to import post";
      toast.error(message);
    },
  });

  const posts = postsData?.data || [];
  const filteredPosts = posts.filter((post) => {
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === "all" || post.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleImport = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      importMutation.mutate(file);
      e.target.value = "";
    }
  };

  const handleExportAll = async () => {
    try {
      const res = await authenticatedFetch(`/api/admin/posts/export?type=${postType}`);
      if (!res.ok) throw new Error("Failed to export posts");

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${postType}-posts-export.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Posts exported successfully");
    } catch {
      toast.error("Failed to export posts");
    }
  };

  const typeLabel = type === "case-study" ? "Case Studies" : "Blog Posts";
  const singularLabel = type === "case-study" ? "Case Study" : "Blog Post";

  return (
    <AdminLayout postCounts={countsData?.data}>
      <div className="p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-semibold text-foreground">{typeLabel}</h1>
          <div className="flex items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept=".md"
              className="hidden"
              onChange={handleFileChange}
            />
            <Button
              variant="outline"
              size="sm"
              onClick={handleImport}
              disabled={importMutation.isPending}
            >
              <Upload className="h-4 w-4 mr-2" />
              Import .md
            </Button>
            <Button variant="outline" size="sm" onClick={handleExportAll}>
              <Download className="h-4 w-4 mr-2" />
              Export All
            </Button>
            <Link to={`/posts/${type}/new`}>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                New {singularLabel}
              </Button>
            </Link>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-card border border-border rounded-lg">
          <div className="px-4 py-3 border-b border-border flex items-center gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search posts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 text-sm bg-secondary border-0 rounded-md focus:ring-2 focus:ring-ring focus:outline-none"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="text-sm bg-secondary border-0 rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:outline-none"
            >
              <option value="all">All Status</option>
              <option value="published">Published</option>
              <option value="draft">Draft</option>
            </select>
          </div>

          {/* Posts list */}
          {isLoading ? (
            <div className="px-4 py-12 text-center text-muted-foreground">Loading...</div>
          ) : filteredPosts.length === 0 ? (
            <div className="px-4 py-12 text-center text-muted-foreground">
              {searchQuery || statusFilter !== "all"
                ? "No posts match your filters"
                : `No ${typeLabel.toLowerCase()} yet. Create your first one!`}
            </div>
          ) : (
            <div className="divide-y divide-border">
              {filteredPosts.map((post) => (
                <Link
                  key={post.id}
                  to={`/posts/${type}/${post.id}`}
                  className="px-4 py-3 flex items-center hover:bg-accent/50 transition-colors group"
                >
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-medium text-foreground truncate">
                      {post.title || "Untitled"}
                    </h3>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {post.published_at
                        ? format(new Date(post.published_at), "MMM d, yyyy")
                        : format(new Date(post.created_at), "MMM d, yyyy")}
                      {post.categories.length > 0 && (
                        <>
                          {" · "}
                          {post.categories.map((c) => c.singular_name || c.name).join(", ")}
                        </>
                      )}
                    </p>
                  </div>
                  <span
                    className={`text-xs px-2 py-1 rounded-full mr-3 ${
                      post.status === "published"
                        ? "bg-success/10 text-success"
                        : "bg-warning/10 text-warning"
                    }`}
                  >
                    {post.status === "published" ? "Published" : "Draft"}
                  </span>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (confirm("Are you sure you want to delete this post?")) {
                        deleteMutation.mutate(post.id);
                      }
                    }}
                    className="p-1.5 text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
